import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSSEApply } from '../components/sitepilot/useSSEApply';

const AIPageAssistant = ({ websiteId, onApplied, currentPageId, currentCode, onCodeApply }) => {
  const defaultWelcome = {
    id: 'sys-welcome',
    role: 'assistant',
    content:
      "You are working with SitePilot – an autonomous Website Builder Web Agent that behaves like a senior full-stack engineer, product designer, QA, and DevOps in one.\n\nMission: Turn your plain-English requests into a fully functional website and its backend services (APIs, database, auth, admin panel, payments, email, analytics) inside this platform.\n\nHow to use me: In this editor, just describe what you want for this site (new pages, sections, forms, flows, or backend features). I will propose concrete changes that can be applied to your website.",
  };

  const [agentMode, setAgentMode] = useState('sitebrain'); // sitebrain only


  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(''); // chat input
  const [loading, setLoading] = useState(false);
  const [pendingActions, setPendingActions] = useState([]);
  const [lastChangeset, setLastChangeset] = useState(null);
  const [autoApply, setAutoApply] = useState(true);

  // Load and persist auto-apply preference per website
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const raw = localStorage.getItem(`sitepilot_auto_apply_${websiteId}`);
      if (raw !== null) {
        setAutoApply(raw === 'true');
      }
    } catch (e) {
      console.error('Failed to load SitePilot auto-apply preference', e);
    }
  }, [websiteId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(`sitepilot_auto_apply_${websiteId}`, String(autoApply));
    } catch (e) {
      console.error('Failed to persist SitePilot auto-apply preference', e);
    }
  }, [autoApply, websiteId]);


  const { events: applyEvents, running: applyStreaming, run: runApplyStream, cancel: cancelApplyStream } = useSSEApply();

  // Load chat history from localStorage on mount (per website)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(`sitepilot_chat_${websiteId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          const sanitized = parsed.filter((m) => m.id !== 'sys-welcome');
          setMessages(sanitized);
        }
      }
    } catch (e) {
      console.error('Failed to load SitePilot chat history', e);
    }
  }, [websiteId]);

  // Persist chat history whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(`sitepilot_chat_${websiteId}`, JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to persist SitePilot chat history', e);
    }
  }, [messages, websiteId]);

  const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
    ? process.env.REACT_APP_BACKEND_URL
    : '';

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !websiteId) return;

    const nextMessages = [
      ...messages,
      { id: `u-${Date.now()}`, role: 'user', content: text },
    ];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      // DevAgent mode: compound schema+workspace execution via backend SSE
      if (agentMode === 'devagent') {
        const token = localStorage.getItem('token');
        await runApplyStream({
          url: `${API_URL}/api/ai/dev-agent/run-stream`,
          token,
          body: { website_id: websiteId, message: text },
        });

        // Refresh editor state
        onApplied?.();

        setMessages([
          ...nextMessages,
          {
            id: `a-dev-${Date.now()}`,
            role: 'assistant',
            content: 'DevAgent: completed the requested changes (schema + workspace where needed).',
          },
        ]);
        return;
      }

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ai/page-agent/plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          website_id: websiteId,
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'AI assistant failed');
      }
      if (data.requires_clarification) {
        const qs = Array.isArray(data.questions) ? data.questions : [];
        const qText = qs.length
          ? `I need a bit more info before I can apply changes:\n\n${qs.map((q, i) => `${i + 1}) ${q}`).join('\n')}`
          : 'I need a bit more info before I can apply changes.';

        setMessages([
          ...nextMessages,
          {
            id: `a-clarify-${Date.now()}`,
            role: 'assistant',
            content: qText,
          },
        ]);
        setLoading(false);
        return;
      }

      const reply = data.assistant_reply || 'Okay, I have proposed some actions for this website.';
      const actions = Array.isArray(data.actions) ? data.actions : [];

      // Append AI reply to chat
      const updatedMessages = [
        ...nextMessages,
        { id: `a-${Date.now()}`, role: 'assistant', content: reply },
      ];

      // If there are actions, either auto-apply or leave them pending based on toggle
      if (actions.length > 0) {
        setPendingActions(actions);
        if (autoApply) {
          const applyResult = await applyActionsInternal(actions);
          if (applyResult && Array.isArray(applyResult.applied)) {
            // Show backend generation explanation (if any)
            const backendGen = applyResult.applied.find((r) => r.type === 'generate_backend');
            if (backendGen && backendGen.explanation) {
              updatedMessages.push({
                id: `a-backend-${Date.now()}`,
                role: 'assistant',
                content: backendGen.explanation,
              });
            }
            // Show OpenHands conversation info (if any)
            const ohConv = applyResult.applied.find((r) => r.type === 'openhands_conversation');
            if (ohConv && (ohConv.conversation_id || ohConv.error)) {
              const text = ohConv.error
                ? `OpenHands: failed to start conversation (${ohConv.error}).`
                : `OpenHands: started a background task for this repo (conversation id: ${ohConv.conversation_id}).`;
              updatedMessages.push({
                id: `a-oh-${Date.now()}`,
                role: 'assistant',
                content: text,
              });
            }
          }
        }
      } else {
        setPendingActions([]);
      }

      setMessages(updatedMessages);
    } catch (err) {
      console.error(err);

      let message = err?.message || 'Something went wrong while talking to the AI assistant. Please try again.';

      // Make common auth/network errors actionable
      const lower = String(message).toLowerCase();
      if (lower.includes('not authenticated') || lower.includes('invalid token') || lower.includes('unauthorized') || lower.includes('401')) {
        message = 'Authentication expired. Please log in again.';
      }

      setMessages([
        ...messages,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: message,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Internal helper that actually calls the apply API for a given actions array
  const applyActionsInternal = async (actionsToApply) => {
    if (!websiteId || !actionsToApply.length) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Phase 2: show progress events while applying
      await runApplyStream({
        url: `${API_URL}/api/ai/page-agent/apply-stream`,
        token,
        body: { website_id: websiteId, actions: actionsToApply },
      });

      const res = await fetch(`${API_URL}/api/ai/page-agent/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ website_id: websiteId, actions: actionsToApply }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to apply AI changes');
      }
      setPendingActions([]);

      if (data.changeset_id) {
        setLastChangeset({
          id: data.changeset_id,
          summary: data.summary,
          created_at: data.created_at,
        });
      }
      if (onApplied) onApplied(data);
    } catch (err) {
      console.error(err);
      const msg = err?.message || 'Failed to apply AI changes';
      const lower = String(msg).toLowerCase();
      if (lower.includes('not authenticated') || lower.includes('invalid token') || lower.includes('unauthorized') || lower.includes('401')) {
        alert('Authentication expired. Please log in again.');
      } else {
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Kept for potential manual apply button in future (currently unused)
  const applyActions = async () => {
    await applyActionsInternal(pendingActions);
  };

  return (
    <div className="mt-6 border-t border-zinc-800 pt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-400" />
          <span className="text-xs font-semibold text-zinc-200 uppercase tracking-wide">
            AI Page Assistant
          </span>
        </div>
        <button
          type="button"
          onClick={() => setAutoApply((v) => !v)}
          className="flex items-center gap-1 px-2 py-1 rounded-full border border-zinc-700 text-[10px] text-zinc-300 hover:border-violet-500 hover:text-white"
        >
          <span
            className={
              autoApply
                ? 'inline-block w-2 h-2 rounded-full bg-emerald-400'
                : 'inline-block w-2 h-2 rounded-full bg-zinc-500'
            }
          />
          {autoApply ? 'Auto-apply: ON' : 'Auto-apply: OFF'}
        </button>
      </div>
      <div className="mb-3 flex items-center justify-between gap-3 text-[11px] text-zinc-400">
        <span className="truncate">
          Describe what you want for this site (pages, sections, forms, or backend features).
        </span>
        <span className="shrink-0 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] text-zinc-300">
          Auto-apply {autoApply ? 'applies safe changes immediately' : 'will only propose a plan'}
        </span>
      </div>

      {/* Phase 2: Progress events */}
      <div className="mb-3 rounded-md border border-zinc-800 bg-zinc-950/40 p-2">
        {/* Agent Mode Label (SiteBrain only) */}
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-zinc-200">
            <span className="text-zinc-400">Agent Mode</span>
            <span className="text-xs px-2 py-1 rounded bg-zinc-800 text-white">SiteBrain</span>
          </div>
        </div>

        {/* Progress header */}
        <div className="mb-1 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">Progress</div>
          {applyStreaming && (
            <button
              type="button"
              onClick={cancelApplyStream}
              className="text-[10px] text-zinc-300 hover:text-white underline"
            >
              Cancel
            </button>
          )}
        </div>
        <div className="max-h-24 overflow-auto space-y-1 text-xs">
          {applyEvents.length === 0 ? (
            <div className="text-zinc-500 text-xs">No events yet.</div>
          ) : (
            applyEvents.slice(-30).map((e, idx) => (
              <div key={idx} className="text-zinc-300">
                <span className="text-zinc-500 mr-2">{e.event}</span>
                {e.type ? <span className="text-zinc-200">{e.type}</span> : null}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Page code viewer removed as requested */}

      {(messages.length > 0 || loading) && (
        <div className="h-40 overflow-y-auto rounded-md bg-zinc-900/60 border border-zinc-800 p-2 mb-2 text-xs space-y-1">
          {messages.map((m) => (
            <div
              key={m.id}
              className={
                m.role === 'user'
                  ? 'text-zinc-100'
                  : 'text-zinc-300 border border-zinc-800 rounded px-2 py-1 bg-zinc-900/70'
              }
            >
              <strong className="mr-1">{m.role === 'user' ? 'You:' : 'AI:'}</strong>
              <span>{m.content}</span>
            </div>
          ))}
          {loading && <div className="text-zinc-500 text-xs">Thinking...</div>}
        </div>
      )}

      {/* Bolt-style Plan / Proposed changes view */}
      {pendingActions.length > 0 && (
        <div className="mb-3 rounded-md bg-zinc-900/70 border border-violet-700/60 p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-semibold text-violet-200">Proposed changes</span>
            <span className="text-[10px] text-zinc-500">{pendingActions.length} actions</span>
          </div>
          <ol className="list-decimal list-inside space-y-1 text-[11px] text-zinc-200 max-h-20 overflow-y-auto">
            {pendingActions.map((action, idx) => (
              <li key={idx}>
                <span className="font-semibold mr-1">{action.type}</span>
                <span className="text-zinc-400">
                  {action.payload?.description ? `– ${action.payload.description}` : ''}
                </span>
              </li>
            ))}
          </ol>
          {!autoApply && (
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                className="h-7 px-3 text-[11px]"
                disabled={loading}
                onClick={() => applyActionsInternal(pendingActions)}
              >
                Apply all changes
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mb-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="e.g. Add a pricing page with 3 plans and a contact form"
          className="flex-1 px-2 py-1.5 rounded bg-zinc-900 border border-zinc-800 text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-violet-500"
        />
        <Button size="sm" disabled={loading} onClick={sendMessage} className="text-xs px-3">
          Ask AI
        </Button>
      </div>

      {lastChangeset && (
        <div className="mb-2 rounded bg-zinc-900/60 border border-amber-500/40 p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-semibold text-amber-300">Last Changeset</span>
            <span className="text-[10px] text-zinc-500">{new Date(lastChangeset.created_at).toLocaleTimeString()}</span>
          </div>
          <p className="text-[11px] text-zinc-300 mb-1 truncate">
            {lastChangeset.summary || 'Changes applied'} (ID: {lastChangeset.id.slice(0, 8)}…)
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 px-2 py-1 text-[11px] border-amber-500/60 text-amber-200 hover:bg-amber-500/10"
              disabled={loading}
              data-testid="changeset-approve"
              onClick={async () => {
                try {
                  setLoading(true);
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/ai/page-agent/changesets/${lastChangeset.id}/approve`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  });
                  if (!res.ok) throw new Error('Failed to approve');
                  await res.json();
                  alert('Changeset approved');
                } catch (err) {
                  console.error(err);
                  alert('Failed to approve');
                } finally {
                  setLoading(false);
                }
              }}
            >
              Approve
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 px-2 py-1 text-[11px] border-amber-500/60 text-amber-200 hover:bg-amber-500/10"
              disabled={loading}
              data-testid="changeset-undo"
              onClick={async () => {
                try {
                  setLoading(true);
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/ai/page-agent/undo-last`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ website_id: websiteId }),
                  });
                  if (!res.ok) throw new Error('Failed to undo changes');
                  await res.json();
                  alert('Last changeset undone');
                  setLastChangeset(null);
                  onApplied?.();
                } catch (err) {
                  console.error(err);
                  alert('Failed to undo changes');
                } finally {
                  setLoading(false);
                }
              }}
            >
              Undo
            </Button>
          </div>

          </p>
{null}
        </div>
      )}
    </div>
  );
};

export default AIPageAssistant;
